import { useState } from 'react';
import { ChevronDown, ChevronRight, Copy, Trash2, Check } from 'lucide-react';
import { formatPrice, cn } from '../lib/utils';
import { getEditionFullName } from '../lib/utils';
import { ConfirmDialog } from './ConfirmDialog';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import type { CartSession } from '../lib/types';

const CONDITION_BADGE_CLASS: Record<string, string> = {
  nm: 'badge-nm',
  lp: 'badge-lp',
  mp: 'badge-mp',
  hp: 'badge-hp',
  dm: 'badge-dm',
};

interface SessionHistoryProps {
  sessions: CartSession[];
  isLoading: boolean;
  onDeleteSession: (id: string) => void;
  onClearAll: () => void;
  onCopyToClipboard: (session: CartSession) => Promise<boolean>;
  onClose: () => void;
}

export function SessionHistory({
  sessions,
  isLoading,
  onDeleteSession,
  onClearAll,
  onCopyToClipboard,
  onClose,
}: SessionHistoryProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const handleCopy = async (session: CartSession) => {
    const ok = await onCopyToClipboard(session);
    if (ok) {
      setCopiedId(session.id);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[90vh] w-full max-w-lg flex-col p-0">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-outline px-4 py-3 pr-12">
          <DialogTitle>Session History</DialogTitle>
          {sessions.length > 0 && (
            <button
              onClick={() => setShowClearConfirm(true)}
              className="rounded-full px-3 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/10 transition-colors"
            >
              Clear All
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto px-4 py-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-outline border-t-primary" />
            </div>
          ) : sessions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-foreground">No completed sessions</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Sessions appear here after you complete them
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {sessions.map((session) => {
                const total = session.items.reduce(
                  (sum, i) => sum + i.finalPrice,
                  0
                );
                const isExpanded = expandedId === session.id;

                return (
                  <div
                    key={session.id}
                    className="rounded-lg border border-outline bg-muted"
                  >
                    {/* Session header */}
                    <button
                      onClick={() =>
                        setExpandedId(isExpanded ? null : session.id)
                      }
                      className="flex w-full items-center justify-between px-3 py-2.5 text-left"
                    >
                      <div className="flex items-center gap-2">
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <Badge
                              variant={session.mode === 'buy' ? 'tertiary' : 'default'}
                              className="px-1.5 py-0.5 text-xs"
                            >
                              {session.mode === 'buy' ? 'BUY' : 'SELL'}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {formatDate(
                                session.completedAt || session.createdAt
                              )}
                            </span>
                          </div>
                          {session.label && (
                            <p className="mt-0.5 text-xs text-muted-foreground">
                              {session.label}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <span
                          className={cn(
                            'text-sm font-semibold',
                            session.mode === 'buy'
                              ? 'text-tertiary'
                              : 'text-primary'
                          )}
                        >
                          {formatPrice(total)}
                        </span>
                        <span className="block text-xs text-muted-foreground">
                          {session.items.length} card
                          {session.items.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </button>

                    {/* Expanded items */}
                    {isExpanded && (
                      <div className="border-t border-outline px-3 py-2">
                        <div className="space-y-1.5">
                          {session.items.map((item) => (
                            <div
                              key={item.id}
                              className="flex items-center justify-between text-xs"
                            >
                              <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                <span
                                  className={cn(
                                    'flex-shrink-0 rounded px-1 py-0.5 text-xs font-medium',
                                    CONDITION_BADGE_CLASS[item.condition] ||
                                      'badge-nm'
                                  )}
                                >
                                  {item.condition.toUpperCase()}
                                </span>
                                <span className="truncate text-muted-foreground">
                                  {item.cardName}
                                </span>
                                {item.edition && (
                                  <span className="flex-shrink-0 text-amber-600">
                                    [{getEditionFullName(item.edition)}]
                                  </span>
                                )}
                              </div>
                              <div className="flex-shrink-0 text-right">
                                {item.finalPrice !== item.originalPrice && (
                                  <span className="mr-1 text-muted-foreground line-through">
                                    {formatPrice(item.originalPrice)}
                                  </span>
                                )}
                                <span className="font-medium text-foreground">
                                  {formatPrice(item.finalPrice)}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Actions */}
                        <div className="mt-3 flex gap-2">
                          <button
                            onClick={() => handleCopy(session)}
                            className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-card px-3 py-2 text-xs font-medium text-muted-foreground hover:bg-outline/20 transition-colors"
                          >
                            {copiedId === session.id ? (
                              <>
                                <Check className="h-3.5 w-3.5 text-green-500" />
                                <span className="text-green-500">Copied!</span>
                              </>
                            ) : (
                              <>
                                <Copy className="h-3.5 w-3.5" />
                                Copy
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => onDeleteSession(session.id)}
                            className="flex items-center justify-center gap-1.5 rounded-full bg-destructive/10 px-3 py-2 text-xs font-medium text-destructive hover:bg-destructive/20 transition-colors"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {showClearConfirm && (
          <ConfirmDialog
            title="Clear All History?"
            message={`This will permanently delete ${sessions.length} completed session${sessions.length !== 1 ? 's' : ''}.`}
            confirmLabel="Clear All"
            onConfirm={() => {
              onClearAll();
              setShowClearConfirm(false);
            }}
            onCancel={() => setShowClearConfirm(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
